import * as THREE from 'three';

const vertexShader = `
varying vec2 vUv;
varying vec3 vPosition;

void main() {
  vPosition = position;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = `
varying vec2 vUv;
varying vec3 vPosition;

uniform float uTime;
uniform vec3  uColor;
uniform float uSpeed;
uniform float uScale;
uniform float uRotation;
uniform float uNoiseIntensity;

const float e = 2.71828182845904523536;

float noise(vec2 texCoord) {
  float G = e;
  vec2  r = (G * sin(G * texCoord));
  return fract(r.x * r.y * (1.0 + texCoord.x));
}

vec2 rotateUvs(vec2 uv, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  mat2  rot = mat2(c, -s, s, c);
  return rot * uv;
}

void main() {
  float rnd        = noise(gl_FragCoord.xy);
  vec2  uv         = rotateUvs(vUv * uScale, uRotation);
  vec2  tex        = uv * uScale;
  float tOffset    = uSpeed * uTime;

  tex.y += 0.03 * sin(8.0 * tex.x - tOffset);

  float pattern = 0.6 +
                  0.4 * sin(5.0 * (tex.x + tex.y +
                                   cos(3.0 * tex.x + 5.0 * tex.y) +
                                   0.02 * tOffset) +
                           sin(20.0 * (tex.x + tex.y - 0.1 * tOffset)));

  vec4 col = vec4(uColor, 1.0) * vec4(pattern) - rnd / 15.0 * uNoiseIntensity;
  col.a = 1.0;
  gl_FragColor = col;
}
`;

function hexToNormalizedRGB(hex) {
  const cleaned = hex.replace('#', '');
  return [
    parseInt(cleaned.slice(0, 2), 16) / 255,
    parseInt(cleaned.slice(2, 4), 16) / 255,
    parseInt(cleaned.slice(4, 6), 16) / 255
  ];
}

export default {
  init() {
    const silkContainers = document.querySelectorAll('[data-silk-background]');
    if (!silkContainers.length) return;

    silkContainers.forEach((container) => {
      const config = {
        speed: Number(container.dataset.silkSpeed) || 5,
        scale: Number(container.dataset.silkScale) || 1,
        color: container.dataset.silkColor || '#7B7481',
        noiseIntensity: Number(container.dataset.silkNoiseIntensity) || 1.5,
        rotation: Number(container.dataset.silkRotation) || 0
      };

      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || 500;

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(
        width / -2,
        width / 2,
        height / 2,
        height / -2,
        0.1,
        1000
      );
      camera.position.z = 1;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0xfcfbfc, 1);

      container.style.position = 'relative';
      container.style.overflow = 'hidden';
      const canvas = renderer.domElement;
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.zIndex = '1';
      container.insertBefore(canvas, container.firstChild);

      const rgb = hexToNormalizedRGB(config.color);
      const uniforms = {
        uTime: { value: 0 },
        uSpeed: { value: config.speed },
        uScale: { value: config.scale },
        uNoiseIntensity: { value: config.noiseIntensity },
        uColor: { value: new THREE.Vector3(rgb[0], rgb[1], rgb[2]) },
        uRotation: { value: config.rotation }
      };

      const material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
        side: THREE.DoubleSide
      });

      const geometry = new THREE.PlaneGeometry(width, height, 1, 1);
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      let animationId = null;
      let lastTime = Date.now();

      const animate = () => {
        animationId = requestAnimationFrame(animate);

        const now = Date.now();
        const deltaTime = (now - lastTime) / 1000;
        lastTime = now;

        uniforms.uTime.value += 0.1 * deltaTime;
        renderer.render(scene, camera);
      };

      animate();

      const handleResize = () => {
        const newWidth = container.clientWidth || window.innerWidth;
        const newHeight = container.clientHeight || 500;

        camera.left = newWidth / -2;
        camera.right = newWidth / 2;
        camera.top = newHeight / 2;
        camera.bottom = newHeight / -2;
        camera.updateProjectionMatrix();

        renderer.setSize(newWidth, newHeight);
        mesh.geometry.dispose();
        mesh.geometry = new THREE.PlaneGeometry(newWidth, newHeight, 1, 1);
      };

      window.addEventListener('resize', handleResize);

      container.dataset.silkAnimationId = animationId;
      container.dataset.silkRenderer = renderer;
    });
  }
};
